'use client';
import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Lock, ChevronRight, X, Rocket } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';

interface ChecklistItem {
  id: string;
  labelKey: string;
  descKey: string;
  href?: string;
  completed: boolean;
}

const STORAGE_KEY = 'aimaker_onboarding_checklist';

function getStoredProgress(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

export default function OnboardingChecklist() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const oc = t?.onboardingChecklist;

  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [progress, setProgress] = useState<Record<string, boolean>>({});
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const stored = getStoredProgress();
    const wasDismissed = localStorage.getItem('aimaker_checklist_dismissed') === 'true';
    setProgress(stored);
    setDismissed(wasDismissed);
    if (user && !stored['create_account']) {
      const updated = { ...stored, create_account: true };
      setProgress(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
  }, [user]);

  const CHECKLIST: ChecklistItem[] = [
    { id: 'create_account', labelKey: 'createAccount', descKey: 'createAccountDesc', completed: !!progress['create_account'] },
    { id: 'choose_journey', labelKey: 'chooseJourney', descKey: 'chooseJourneyDesc', href: '/', completed: !!progress['choose_journey'] },
    { id: 'dream_dna', labelKey: 'completeDreamDna', descKey: 'completeDreamDnaDesc', href: '/dream-dashboard', completed: !!progress['dream_dna'] },
    { id: 'business_builder', labelKey: 'businessBuilder', descKey: 'businessBuilderDesc', href: '/business-builder', completed: !!progress['business_builder'] },
    { id: 'first_generation', labelKey: 'firstGeneration', descKey: 'firstGenerationDesc', href: '/create-page', completed: !!progress['first_generation'] },
    { id: 'complete_profile', labelKey: 'completeProfile', descKey: 'completeProfileDesc', href: '/account', completed: !!progress['complete_profile'] },
    { id: 'ready_to_launch', labelKey: 'readyToLaunch', descKey: 'readyToLaunchDesc', completed: !!progress['ready_to_launch'] },
  ];

  const completedCount = CHECKLIST.filter(i => i.completed).length;
  const totalCount = CHECKLIST.length;
  const progressPct = Math.round((completedCount / totalCount) * 100);
  const allDone = completedCount === totalCount;

  const markComplete = (id: string) => {
    const updated = { ...progress, [id]: true };
    setProgress(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    const othersDone = CHECKLIST.filter(i => i.id !== 'ready_to_launch').every(i => updated[i.id]);
    if (othersDone) {
      const final = { ...updated, ready_to_launch: true };
      setProgress(final);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(final));
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('aimaker_checklist_dismissed', 'true');
  };

  if (!mounted || dismissed || !user) return null;
  if (allDone && completedCount === totalCount) return null;

  return (
    <>
      {/* Floating trigger */}
      {!visible && (
        <button
          onClick={() => setVisible(true)}
          className="fixed bottom-6 left-4 sm:left-6 z-[9997] flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-primary/30 shadow-lg hover:border-primary/60 transition-all group"
          style={{ animation: 'checklistPop 0.3s ease-out' }}
        >
          <style>{`
            @keyframes checklistPop {
              from { opacity: 0; transform: scale(0.8); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
          <div className="relative w-6 h-6">
            <svg viewBox="0 0 24 24" className="w-6 h-6 -rotate-90">
              <circle cx="12" cy="12" r="10" fill="none" stroke="rgba(167,139,250,0.2)" strokeWidth="2" />
              <circle
                cx="12" cy="12" r="10" fill="none"
                stroke="#a78bfa" strokeWidth="2"
                strokeDasharray={`${progressPct * 0.628} 62.8`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-700 text-primary">{progressPct}%</span>
          </div>
          <span className="text-xs font-600 text-foreground">{oc?.title || 'Getting Started'}</span>
          <ChevronRight size={13} className="text-muted-foreground group-hover:text-primary transition-colors" />
        </button>
      )}

      {/* Checklist panel */}
      {visible && (
        <div
          className="fixed bottom-6 left-4 sm:left-6 z-[9997] w-80 rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
          style={{ animation: 'checklistSlideUp 0.2s ease-out' }}
        >
          <style>{`
            @keyframes checklistSlideUp {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-primary/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Rocket size={15} className="text-primary" />
                <p className="text-sm font-700 text-foreground">{oc?.title || 'Getting Started'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDismiss}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {oc?.skip || 'Skip'}
                </button>
                <button
                  onClick={() => setVisible(false)}
                  className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
                >
                  <X size={13} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="text-xs font-600 text-primary shrink-0">{completedCount}/{totalCount}</span>
            </div>
          </div>

          {/* Checklist items */}
          <div className="p-3 flex flex-col gap-1 max-h-80 overflow-y-auto">
            {CHECKLIST.map((item, i) => {
              const isLocked = i > 0 && !CHECKLIST[i - 1].completed && !item.completed;
              const label = (oc as any)?.[item.labelKey] || item.labelKey;
              const desc = (oc as any)?.[item.descKey] || item.descKey;
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all
                    ${item.completed ? 'opacity-60' : isLocked ? 'opacity-40' : 'hover:bg-muted/20 cursor-pointer'}
                  `}
                  onClick={() => {
                    if (!item.completed && !isLocked) {
                      markComplete(item.id);
                      if (item.href) {
                        setVisible(false);
                      }
                    }
                  }}
                >
                  <div className="shrink-0">
                    {item.completed ? (
                      <CheckCircle2 size={18} className="text-mint" />
                    ) : isLocked ? (
                      <Lock size={16} className="text-muted-foreground/40" />
                    ) : (
                      <Circle size={18} className="text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-500 ${item.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {label}
                    </p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                  {!item.completed && !isLocked && item.href && (
                    <Link
                      href={item.href}
                      onClick={e => { e.stopPropagation(); markComplete(item.id); setVisible(false); }}
                      className="shrink-0 p-1 rounded-lg text-muted-foreground hover:text-primary transition-colors"
                    >
                      <ChevronRight size={14} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <div className="px-4 py-3 border-t border-border bg-muted/10">
            <p className="text-[10px] text-muted-foreground text-center">
              {oc?.footerNote || 'Complete all steps to unlock your full potential with AIMaker Studio'}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
